import { queryClient, apiRequest, CACHE_TIMES } from "@/lib/queryClient";
import {
  customerOrderSummaryQuery,
  getStoredCustomerIdentity,
} from "@/lib/customer-order-queries";

export const PREFETCH_QUERIES = {
  home: [
    { queryKey: ["/api/quotations"] },
    { queryKey: ["/api/public/settings"] },
    { queryKey: ["/api/public/watches"] },
  ],
  comproOro: [
    { queryKey: ["/api/quotations"] },
    { queryKey: ["/api/coins-config"] },
  ],
  orologeria: [
    { queryKey: ["/api/public/watches"] },
    { queryKey: ["/api/quotations"] },
  ],
  investimenti: [
    { queryKey: ["/api/investments"] },
    { queryKey: ["/api/quotations"] },
  ],
  gioielleria: [
    { queryKey: ["/api/public/jewelry"] },
    { queryKey: ["/api/jewelry-brands"] },
  ],
  carrello: [
    { queryKey: ["/api/cart"] },
  ],
  // Customer queries are identity-scoped below. Keeping no anonymous query
  // descriptor here prevents a hover from prefetching private data.
  areaClienti: [],
  chiSiamo: [
    { queryKey: ["/api/staff-profiles"] },
  ],
};

export const CRM_PREFETCH_QUERIES = {
  dashboard: [
    { queryKey: ["/api/crm/dashboard-stats"] },
  ],
  customers: [
    { queryKey: ["/api/crm/customers"] },
  ],
  watches: [
    { queryKey: ["/api/crm/luxury-watches"] },
  ],
  valuations: [
    { queryKey: ["/api/crm/valuations"] },
  ],
  appointments: [
    { queryKey: ["/api/crm/appointments"] },
  ],
  jewelry: [
    { queryKey: ["/api/crm/jewelry-listings"] },
  ],
  investments: [
    { queryKey: ["/api/crm/investments"] },
  ],
  email: [
    { queryKey: ["/api/crm/inbox"] },
  ],
};

function prefetchQueries(queries: Array<{ queryKey: readonly unknown[]; queryFn?: (context: { signal: AbortSignal }) => Promise<unknown>; staleTime?: number }>) {
  queries.forEach(({ queryKey, queryFn, staleTime }) => {
    const existingData = queryClient.getQueryData(queryKey);
    if (!existingData) {
      queryClient.prefetchQuery({
        queryKey,
        queryFn: queryFn ?? (({ signal }) => {
          // The generic entries have a one-segment URL. Customer summary uses
          // its fixed route in customerOrderSummaryQuery instead.
          const url = String(queryKey[0]);
          return apiRequest("GET", url, undefined, { signal }).then(res => res.json());
        }),
        staleTime: staleTime ?? CACHE_TIMES.catalog,
      });
    }
  });
}

export function prefetchForRoute(route: keyof typeof PREFETCH_QUERIES) {
  if (route === "areaClienti") {
    const customerId = getStoredCustomerIdentity();
    if (!customerId) return;
    const summary = customerOrderSummaryQuery(customerId);
    if (!queryClient.getQueryData(summary.queryKey)) {
      queryClient.prefetchQuery(summary);
    }
    return;
  }
  const queries = PREFETCH_QUERIES[route];
  if (queries) prefetchQueries(queries);
}

export function prefetchCrmRoute(route: keyof typeof CRM_PREFETCH_QUERIES) {
  const queries = CRM_PREFETCH_QUERIES[route];
  if (queries) prefetchQueries(queries);
}

export function createMouseEnterHandler(route: keyof typeof PREFETCH_QUERIES) {
  let timeout: NodeJS.Timeout | null = null;
  let prefetched = false;
  
  return {
    onMouseEnter: () => {
      if (prefetched) return;
      timeout = setTimeout(() => {
        prefetchForRoute(route);
        prefetched = true;
      }, 100);
    },
    onMouseLeave: () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    },
  };
}

export function createCrmMouseEnterHandler(route: keyof typeof CRM_PREFETCH_QUERIES) {
  let timeout: NodeJS.Timeout | null = null;
  let prefetched = false;
  
  return {
    onMouseEnter: () => {
      if (prefetched) return;
      timeout = setTimeout(() => {
        prefetchCrmRoute(route);
        prefetched = true;
      }, 100);
    },
    onMouseLeave: () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    },
  };
}
